<template>
  <div class="i-user-information">
    <div class="hero">
      <img
        :src="heroImage"
        alt="Default banner image"
      />
      <div class="initials">{{ userStore.getUserInitials }}</div>
    </div>
    <div class="info">
      <iBaseHeader
        :highlight-text="userStore.getFullName || ''"
        :font-size-highlighted-text="isDesktop ? '32px' : '28px'"
        text=""
        align="start"
      />
      <div
        v-for="(data, index) in infoData.filter((i) => !!i.value)"
        :key="index"
        class="detail"
      >
        <img
          :src="data.icon"
          :alt="data.altText"
        />
        {{ data.value }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useUserStore } from "@/stores/user"
import { useReportStore } from "@/stores/report"
import { breakpointLg } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseHeader from "@/components/base/BaseHeader.vue"

// ASSETS
import heroImage from "@/assets/home/summary-hero.png"
import iconLocation from "@/assets/icons/icon-marker-pin.svg"
import iconEmail from "@/assets/icons/icon-mail.svg"
import iconPhone from "@/assets/icons/icon-phone.svg"
import iconGender from "@/assets/icons/icon-user.svg"

// STORES AND COMPOSABLES
const userStore = useUserStore()
const reportStore = useReportStore()
const isDesktop = useIsScreenSize(breakpointLg)

// COMPUTED
const infoData = computed(() => {
  const dataArray = [
    { icon: iconLocation, altText: "Location pin icon", value: reportStore.getLocation },
    { icon: iconEmail, altText: "Email icon", value: userStore.getEmail },
    {
      icon: iconGender,
      altText: "Gender icon",
      value: userStore.getGender ? userStore.getGender.charAt(0).toUpperCase() + userStore.getGender.slice(1) : null
    }
  ]

  if (userStore.getPhoneNumber)
    dataArray.push({ icon: iconPhone, altText: "Cell phone icon", value: userStore.getPhoneNumber })

  return dataArray
})
</script>

<style scoped lang="scss">
.i-user-information {
  @include flex(column, start, stretch, $gap: 55px);

  width: 100%;
  height: 100%;

  @media screen and (min-width: $breakpoint-lg) {
    border-bottom: 1px solid $zumthor100;
    border-right: 1px solid $zumthor100;
  }

  .hero {
    position: relative;

    img {
      width: 100%;
      max-height: 150px;
      object-fit: cover;

      @media screen and (min-width: $breakpoint-sm) {
        max-height: 250px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        max-height: 115px;
      }
    }

    .initials {
      position: absolute;

      @include flex(row, center, center);
      @include border-radius-rounded;
      @include display-xs;

      color: $white;
      font-weight: 600;
      width: 85px;
      height: 85px;
      background-color: $pickle-bluewood900;
      border: 5px solid $white;
      bottom: 0;
      left: 20px;
      transform: translate(0, 50%);
    }
  }

  .info {
    @include flex(column, start, stretch, $gap: 8px);

    margin: 0 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid $zumthor100;

    @media screen and (min-width: $breakpoint-lg) {
      border: unset;
    }

    .detail {
      @include flex(row, start, center, $gap: 12px);
      @include text-md;

      font-weight: 400;
      color: $pickle-bluewood800;
      overflow-wrap: anywhere;

      img {
        flex: 0 0 20px;
        width: 20px;
        height: 20px;
      }
    }
  }
}
</style>
